/**
 * Storefront entry point for wholesale bulk pricing.
 *
 * This file owns only two things: reading the ladder out of the page, and
 * exposing it to the other storefront scripts. Every pricing decision comes
 * from `b2b-bulk-ladder.js`, which the discount Function and the metafield push
 * script import as well, so what the storefront shows and what checkout charges
 * cannot drift apart.
 *
 * The ladder is rendered into the page by `snippets/b2b-bulk-pricing-config.liquid`
 * from the shop metafield `b2b.bulk_ladder`. An unseeded shop resolves to an
 * empty ladder and shows no savings, which is the safe direction to fail:
 * advertising a discount checkout will not honour is worse than advertising
 * none.
 *
 * Loaded as a module. The bare specifier below is resolved by the import map in
 * `layout/theme.liquid`, which Liquid fills in with the versioned asset URL.
 * Relying on a relative path instead would make the browser guess at the asset
 * URL on Shopify's CDN.
 */

import {
  EMPTY_LADDER,
  lineTotalForDiscount as ladderLineTotal,
  normalizeLadder,
  priceForDiscount as ladderPrice,
  tierForQuantity as ladderTier,
} from "b2b-bulk-ladder"

const readConfig = () => {
  const el = document.querySelector("[data-b2b-bulk-pricing-config]")
  if (!el) return EMPTY_LADDER

  try {
    return JSON.parse(el.textContent)
  } catch (_error) {
    // A malformed config is a seeding fault, not a buyer's problem. List price
    // is correct and checkout will agree, because the Function reads the same
    // metafield and normalises it the same way.
    return EMPTY_LADDER
  }
}

/** The page's ladder, clamped, filtered and sorted. */
const config = normalizeLadder(readConfig())

/**
 * The deepest tier a product quantity reaches, or null below the first rung.
 *
 * @param {number} quantity Aggregate quantity for one product, not one line.
 * @returns {{ min: number, discount: number } | null}
 */
const tierForQuantity = (quantity) => ladderTier(quantity, config)

/**
 * @param {number} baseCents
 * @param {number} discount
 * @returns {number}
 */
const priceForDiscount = (baseCents, discount) =>
  ladderPrice(baseCents, discount, config)

/**
 * @param {number} baseCents
 * @param {number} quantity
 * @returns {number}
 */
const priceForQuantity = (baseCents, quantity) => {
  const tier = tierForQuantity(quantity)
  return tier
    ? priceForDiscount(baseCents, tier.discount)
    : Number(baseCents) || 0
}

/**
 * @param {number} baseCents
 * @param {number} quantity
 * @param {number} discount
 * @returns {number}
 */
const lineTotalForDiscount = (baseCents, quantity, discount) =>
  ladderLineTotal(baseCents, quantity, discount, config)

// Reuse the shared money formatter (b2b-core loads first, and this is called
// lazily, so the lookup happens well after that script has run).
const formatMoney = (cents, format) => window.B2B.fmtMoney(cents, format)

window.BaerskinB2B = window.BaerskinB2B || {}
window.BaerskinB2B.bulkPricing = {
  config,
  tierForQuantity,
  priceForDiscount,
  priceForQuantity,
  lineTotalForDiscount,
  formatMoney,
}
